// ============================================
// SUPPORT MODAL - Destek Talebi Formu
// ============================================

import { useState, useRef } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import {
  X,
  Send,
  Loader2,
  Upload,
  Image as ImageIcon,
  CheckCircle,
  AlertCircle,
  Trash2,
} from 'lucide-react';
import { useAuthStore } from '@/store/auth';
import { useSendSupportRequest } from '@/hooks';
import { cn } from '@/utils';
import { useTranslation } from '@crew-at-sea/i18n';

interface SupportModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

// Konu kategorileri
const SUPPORT_CATEGORIES = [
  { value: 'TECHNICAL', tKey: 'technical', icon: '🔧' },
  { value: 'JOB_LISTING', tKey: 'jobListing', icon: '📋' },
  { value: 'APPLICATION', tKey: 'application', icon: '📝' },
  { value: 'ACCOUNT', tKey: 'account', icon: '👤' },
  { value: 'CERTIFICATE', tKey: 'certificate', icon: '📜' },
  { value: 'PAYMENT', tKey: 'payment', icon: '💳' },
  { value: 'SUGGESTION', tKey: 'suggestion', icon: '💡' },
  { value: 'OTHER', tKey: 'other', icon: '📌' },
];

// Öncelik seviyeleri
const PRIORITY_LEVELS = [
  { value: 'LOW', tKey: 'low', color: 'text-gray-600 bg-gray-100' },
  { value: 'NORMAL', tKey: 'normal', color: 'text-blue-600 bg-blue-100' },
  { value: 'HIGH', tKey: 'high', color: 'text-orange-600 bg-orange-100' },
  { value: 'URGENT', tKey: 'urgent', color: 'text-red-600 bg-red-100' },
];

export function SupportModal({ open, onOpenChange }: SupportModalProps) {
  const { t } = useTranslation(['panel', 'common']);
  const { user } = useAuthStore();
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Form state
  const [category, setCategory] = useState('');
  const [priority, setPriority] = useState('NORMAL');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [screenshot, setScreenshot] = useState<File | null>(null);
  const [screenshotPreview, setScreenshotPreview] = useState<string | null>(null);

  // UI state
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const sendSupportMutation = useSendSupportRequest();

  // Form reset
  const resetForm = () => {
    setCategory('');
    setPriority('NORMAL');
    setSubject('');
    setMessage('');
    setScreenshot(null);
    setScreenshotPreview(null);
    setError('');
    setSuccess(false);
  };

  // Dosya seçimi
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Max 5MB kontrol
      if (file.size > 5 * 1024 * 1024) {
        setError(t('panel:support.validation.fileSize'));
        return;
      }

      // Sadece resim kabul et
      if (!file.type.startsWith('image/')) {
        setError(t('panel:support.validation.fileType'));
        return;
      }

      setScreenshot(file);
      setError('');

      // Preview oluştur
      const reader = new FileReader();
      reader.onload = () => {
        setScreenshotPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  // Dosya kaldır
  const removeScreenshot = () => {
    setScreenshot(null);
    setScreenshotPreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Form gönderimi
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Validasyon
    if (!category) {
      setError(t('panel:support.validation.categoryRequired'));
      return;
    }

    if (!subject.trim()) {
      setError(t('panel:support.validation.subjectRequired'));
      return;
    }

    if (!message.trim()) {
      setError(t('panel:support.validation.messageRequired'));
      return;
    }

    if (message.trim().length < 20) {
      setError(t('panel:support.validation.messageMinLength'));
      return;
    }

    try {
      // Tarayıcı bilgisi
      const browserInfo = `${navigator.userAgent}`;
      const currentUrl = window.location.href;

      // Screenshot'ı base64'e çevir
      let screenshotBase64: string | undefined;
      if (screenshot) {
        screenshotBase64 = screenshotPreview || undefined;
      }

      await sendSupportMutation.mutateAsync({
        category,
        priority,
        subject: subject.trim(),
        message: message.trim(),
        screenshot: screenshotBase64,
        metadata: {
          userId: user?.id || 'anonymous',
          userEmail: user?.email || 'unknown',
          userRole: user?.role || 'SEAFARER',
          panel: 'user',
          browser: browserInfo,
          url: currentUrl,
          timestamp: new Date().toISOString(),
        },
      });

      setSuccess(true);

      // 3 saniye sonra kapat
      setTimeout(() => {
        resetForm();
        onOpenChange(false);
      }, 3000);
    } catch (err: any) {
      setError(err?.message || t('panel:support.validation.sendError'));
    }
  };

  // Başarılı gönderim ekranı
  if (success) {
    return (
      <Dialog.Root open={open} onOpenChange={onOpenChange}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 bg-black/50 z-[100]" />
          <Dialog.Content className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-white rounded-maritime shadow-lg w-full max-w-md z-[100] p-6">
            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-10 h-10 text-green-600" />
              </div>
              <Dialog.Title className="text-xl font-semibold text-gray-900 mb-2">
                {t('panel:support.success.title')}
              </Dialog.Title>
              <Dialog.Description className="text-gray-600 mb-4">
                {t('panel:support.success.description')}
              </Dialog.Description>
              <p className="text-sm text-gray-500">
                {t('panel:support.success.autoClose')}
              </p>
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    );
  }

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/50 z-[100]" />
        <Dialog.Content className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-white rounded-maritime shadow-lg w-full max-w-lg z-[100] max-h-[90dvh] sm:max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200 sticky top-0 bg-white">
            <div>
              <Dialog.Title className="text-lg font-semibold text-gray-900">
                {t('panel:support.title')}
              </Dialog.Title>
              <Dialog.Description className="text-sm text-gray-500 mt-0.5">
                {t('panel:support.subtitle')}
              </Dialog.Description>
            </div>
            <Dialog.Close className="p-1.5 rounded-maritime hover:bg-gray-100 transition-colors">
              <X className="w-5 h-5 text-gray-500" />
            </Dialog.Close>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="p-4 space-y-4">
            {/* Hata mesajı */}
            {error && (
              <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-maritime text-sm text-red-600">
                <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                <span>{error}</span>
              </div>
            )}

            {/* Konu Kategorisi */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('panel:support.fields.category')} <span className="text-red-500">*</span>
              </label>
              <div className="grid grid-cols-2 gap-2">
                {SUPPORT_CATEGORIES.map((cat) => (
                  <button
                    key={cat.value}
                    type="button"
                    onClick={() => setCategory(cat.value)}
                    className={cn(
                      'flex items-center gap-2 p-3 border rounded-maritime text-left transition-all',
                      category === cat.value
                        ? 'border-maritime-500 bg-maritime-50 text-maritime-700'
                        : 'border-gray-200 hover:border-gray-300 text-gray-700'
                    )}
                  >
                    <span className="text-lg">{cat.icon}</span>
                    <span className="text-sm font-medium">{t(`panel:support.categories.${cat.tKey}`)}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Öncelik */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('panel:support.fields.priority')}
              </label>
              <div className="flex gap-2">
                {PRIORITY_LEVELS.map((level) => (
                  <button
                    key={level.value}
                    type="button"
                    onClick={() => setPriority(level.value)}
                    className={cn(
                      'flex-1 py-2 px-3 text-sm font-medium rounded-maritime border transition-all',
                      priority === level.value
                        ? `${level.color} border-current`
                        : 'border-gray-200 text-gray-600 hover:border-gray-300'
                    )}
                  >
                    {t(`panel:support.priority.${level.tKey}`)}
                  </button>
                ))}
              </div>
            </div>

            {/* Konu Başlığı */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('panel:support.fields.subject')} <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder={t('panel:support.placeholders.subject')}
                className="input w-full"
                maxLength={100}
              />
              <p className="text-xs text-gray-400 mt-1 text-right">
                {subject.length}/100
              </p>
            </div>

            {/* Mesaj */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('panel:support.fields.message')} <span className="text-red-500">*</span>
              </label>
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder={t('panel:support.placeholders.message')}
                className="input w-full min-h-[120px] resize-y"
                maxLength={2000}
              />
              <p className="text-xs text-gray-400 mt-1 text-right">
                {message.length}/2000
              </p>
            </div>

            {/* Ekran Görüntüsü */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('panel:support.fields.screenshot')} <span className="text-gray-400">({t('common:optional')})</span>
              </label>

              {screenshotPreview ? (
                <div className="relative border border-gray-200 rounded-maritime p-2">
                  <img
                    src={screenshotPreview}
                    alt={t('panel:support.fields.screenshot')}
                    className="w-full max-h-40 object-contain rounded"
                  />
                  <button
                    type="button"
                    onClick={removeScreenshot}
                    className="absolute top-3 right-3 p-1.5 bg-red-100 text-red-600 rounded-full hover:bg-red-200 transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                  <p className="text-xs text-gray-500 mt-2 text-center">
                    {screenshot?.name} ({((screenshot?.size || 0) / 1024).toFixed(1)} KB)
                  </p>
                </div>
              ) : (
                <div
                  onClick={() => fileInputRef.current?.click()}
                  className="border-2 border-dashed border-gray-300 rounded-maritime p-6 text-center cursor-pointer hover:border-maritime-400 hover:bg-gray-50 transition-all"
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    onChange={handleFileChange}
                    className="hidden"
                    accept="image/*"
                  />
                  <ImageIcon className="w-8 h-8 mx-auto text-gray-400 mb-2" />
                  <p className="text-sm text-gray-600">
                    {t('panel:support.upload.clickToUpload')}
                  </p>
                  <p className="text-xs text-gray-400 mt-1">
                    PNG, JPG, GIF (Max: 5MB)
                  </p>
                </div>
              )}
            </div>

            {/* Kullanıcı bilgisi notu */}
            <div className="bg-gray-50 rounded-maritime p-3 text-xs text-gray-500">
              <p>
                📧 {t('panel:support.info.replyTo', { email: user?.email })}
              </p>
              <p className="mt-1">
                ℹ️ {t('panel:support.info.autoMetadata')}
              </p>
            </div>

            {/* Butonlar */}
            <div className="flex justify-end gap-3 pt-2 border-t border-gray-200">
              <button
                type="button"
                onClick={() => {
                  resetForm();
                  onOpenChange(false);
                }}
                className="btn-secondary"
                disabled={sendSupportMutation.isPending}
              >
                {t('common:cancel')}
              </button>
              <button
                type="submit"
                className="btn-primary"
                disabled={sendSupportMutation.isPending}
              >
                {sendSupportMutation.isPending ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    {t('panel:support.submitting')}
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4" />
                    {t('panel:support.submit')}
                  </>
                )}
              </button>
            </div>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
